import { InputAdornment, TextField } from '@mui/material';
import { FC, ChangeEvent, ReactNode, useState, useEffect } from 'react';

export interface CurrencyInputProps {
  label?: string;
  name?: string;
  required?: boolean;
  fullWidth?: boolean;
  autoComplete?: string;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  disabled?: boolean;
  value?: number;
  onChange?: (value: number | undefined) => void;
  error?: boolean;
  helperText?: string;
}

// Helper function to format currency with commas
const formatCurrency = (value: string): string => {
  // Remove all non-numeric characters except decimal point
  const numericValue = value.replace(/[^0-9.]/g, '');

  // Handle empty input
  if (numericValue === '') {
    return '';
  }

  // Handle multiple decimal points - keep only the first one
  const parts = numericValue.split('.');
  if (parts.length > 2) {
    const formattedValue = parts[0] + '.' + parts.slice(1).join('');
    return formatCurrency(formattedValue);
  }

  // If there's a decimal point, format the integer part and preserve decimals
  if (parts.length === 2) {
    const integerPart = parts[0];
    const decimalPart = parts[1].slice(0, 2); // Limit to 2 decimal places
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    // Return with decimal point and whatever decimals they've typed
    return `${formattedInteger}.${decimalPart}`;
  }

  // No decimal point, just format with commas
  return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// Helper function to format currency for display (adds trailing zeros for complete decimal places)
const formatCurrencyForDisplay = (value: string): string => {
  const formatted = formatCurrency(value);

  // If it has a decimal point, ensure it has exactly 2 decimal places
  if (formatted.includes('.')) {
    const parts = formatted.split('.');
    const decimalPart = parts[1];

    if (decimalPart.length === 1) {
      return `${parts[0]}.${decimalPart}0`;
    } else if (decimalPart.length === 0) {
      return `${parts[0]}.00`;
    }
    return formatted;
  }

  // For whole numbers, add .00 for complete formatting
  return `${formatted}.00`;
};

// Helper function to parse currency back to number
const parseCurrency = (value: string): number | undefined => {
  const numericValue = value.replace(/[^0-9.]/g, '');
  if (numericValue === '' || numericValue === '.') {
    return undefined;
  }
  const parsed = parseFloat(numericValue);
  return isNaN(parsed) ? undefined : parsed;
};

export const CurrencyInput: FC<CurrencyInputProps> = ({
  label,
  name,
  required,
  fullWidth,
  autoComplete,
  endAdornment,
  value,
  onChange,
  error,
  helperText,
  disabled = false,
}) => {
  const [displayValue, setDisplayValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);

  // Update display value when external value prop changes
  useEffect(() => {
    if (!isFocused) {
      if (value !== undefined && value !== null) {
        setDisplayValue(formatCurrencyForDisplay(String(value)));
      } else {
        setDisplayValue('');
      }
    }
  }, [value, isFocused]);

  // Initialize display value on mount
  useEffect(() => {
    if (value !== undefined && value !== null) {
      setDisplayValue(formatCurrencyForDisplay(String(value)));
    }
  }, []);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const formattedValue = formatCurrency(inputValue);
    const numericValue = parseCurrency(formattedValue);

    setDisplayValue(formattedValue);
    onChange?.(numericValue);
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (value !== undefined && value !== null) {
      // Show the value without auto-padding when focused
      setDisplayValue(formatCurrency(String(value)));
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (value !== undefined && value !== null) {
      // On blur, format with proper decimal places
      setDisplayValue(formatCurrencyForDisplay(String(value)));
    }
  };

  return (
    <TextField
      label={label}
      name={name}
      required={required}
      value={displayValue}
      fullWidth={fullWidth}
      autoComplete={autoComplete}
      type="text"
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      error={error}
      helperText={helperText}
      disabled={disabled}
      slotProps={{
        input: {
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
          endAdornment: endAdornment,
          sx: {
            borderRadius: 3,
          },
        },
      }}
    />
  );
};

export default CurrencyInput;
